import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { tcPost } from './TimeClockDialogs';
import { errorText, hm, tone } from './tcFormat';

/**
 * Per-employee time clock settings: registration required, payroll
 * reference, contract hours, manager right, eID link.
 */
const TimeClockEmployeesTab = ({ t, lang, theme, api, storeName, notify, reloadKey, onLoaded }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = () => {
        tcPost('employees', api).then((response) => {
            if (response?.success) { setData(response.data); setError(''); if (onLoaded) onLoaded(); }
            else setError(errorText(t, response));
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [api, reloadKey]);

    const me = data?.me || {};
    const rows = (data?.employees || []).filter((e) => showInactive || e.is_active);

    const yes = (value) => (value ? '✓' : '');

    const columns = [
        { field: 'name', headerName: t('tc_employee', 'Employee'), flex: 1, minWidth: 180 },
        { field: 'id', headerName: t('tc_code', 'Code'), width: 80 },
        { field: 'store_id', headerName: t('tc_store', 'Store'), width: 150, valueFormatter: (value) => (value ? storeName(value) : t('tc_all_stores', 'All stores')) },
        { field: 'registration_required', headerName: t('tc_registration_required', 'Registration required'), width: 170, align: 'center', headerAlign: 'center', valueFormatter: yes },
        { field: 'employee_ref', headerName: t('tc_employee_ref', 'Payroll number'), width: 140 },
        {
            field: 'contract_minutes_week', headerName: t('tc_contract_week', 'Contract / week'), width: 140, align: 'right', headerAlign: 'right',
            valueFormatter: (value) => (value === null || value === undefined ? '' : hm(value, lang)),
        },
        {
            field: 'can_manage', headerName: t('tc_manager', 'Manager'), width: 150, display: 'flex',
            renderCell: (params) => (params.row.is_administrator
                ? <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('tc_administrator', 'Administrator')}</Typography>
                : (params.value ? '✓' : '')),
        },
        { field: 'has_eid', headerName: 'eID', width: 70, align: 'center', headerAlign: 'center', valueFormatter: yes },
        { field: 'is_active', headerName: t('active', 'Active'), width: 80, align: 'center', headerAlign: 'center', valueFormatter: yes },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', flex: '1 1 320px' }}>
                    {t('tc_employees_hint', 'Employees are the users of this environment. Managers approve corrections and see this page; the Administrator group always may.')}
                </Typography>
                <FormControlLabel sx={{ ml: 'auto', mr: 0, flexShrink: 0 }} control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: 13.5 }}>{t('tc_show_inactive', 'Show inactive')}</Typography>} />
            </Box>
            {error && <Typography sx={{ color: tone.bad, fontSize: 14, mb: 1 }}>{error}</Typography>}
            <Box sx={{ height: 'calc(100vh - 340px)', minHeight: 420 }}>
                <DataGridPro
                    className={theme}
                    rows={rows}
                    columns={columns}
                    loading={!data && !error}
                    density="compact"
                    disableRowSelectionOnClick
                    onRowClick={(params) => setEditing(params.row)}
                    sx={{ fontSize: 13.5, borderRadius: '10px', '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 }, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                    localeText={{ noRowsLabel: t('tc_no_employees', 'No employees.') }}
                />
            </Box>
            <EmployeeDialog open={!!editing} employee={editing} me={me} t={t} api={api}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); if (notify) notify(t('tc_saved', 'Saved'), true); load(); }} />
        </Box>
    );
};

const EmployeeDialog = ({ open, employee, me, onClose, onSaved, t, api }) => {
    const [required, setRequired] = useState(true);
    const [ref, setRef] = useState('');
    const [hours, setHours] = useState('');
    const [manager, setManager] = useState(false);
    const [clearEid, setClearEid] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!employee) return;
        setRequired(employee.registration_required);
        setRef(employee.employee_ref || '');
        const minutes = employee.contract_minutes_week;
        setHours(minutes === null || minutes === undefined ? '' : String(Math.round(minutes / 6) / 10).replace('.', ','));
        setManager(!!employee.can_manage);
        setClearEid(false);
        setError('');
    }, [employee]);

    if (!employee) return null;

    const save = () => {
        const text = hours.trim().replace(',', '.');
        let minutes = '';
        if (text !== '') {
            const value = Number(text);
            if (isNaN(value) || value < 0 || value > 168) { setError(t('tc_err_hours', 'Enter the contract hours per week, for example 38 or 19,5.')); return; }
            minutes = Math.round(value * 60);
        }
        const body = { id: employee.id, registration_required: required, employee_ref: ref.trim(), contract_minutes_week: minutes === '' ? null : minutes };
        if (me.is_administrator && !employee.is_administrator) body.can_manage = manager;
        if (clearEid) body.clear_eid = true;
        setBusy(true);
        tcPost('updateEmployee', api, body).then((response) => {
            setBusy(false);
            if (response?.success) onSaved();
            else setError(errorText(t, response));
        });
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>{employee.name} <Typography component="span" sx={{ color: 'text.secondary' }}>({employee.id})</Typography></DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <FormControlLabel control={<Switch checked={required} onChange={(e) => setRequired(e.target.checked)} />}
                        label={<Box>
                            <Typography sx={{ fontSize: 14 }}>{t('tc_registration_required', 'Registration required')}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('tc_registration_hint', 'Off for people outside the time registration (for example the owner). Shown in the payroll export.')}</Typography>
                        </Box>} />
                    <TextField size="small" label={t('tc_employee_ref', 'Payroll number')} value={ref} onChange={(e) => setRef(e.target.value)}
                        inputProps={{ maxLength: 30 }} helperText={t('tc_employee_ref_hint', 'Number at the payroll office; goes into the payroll export.')} />
                    <TextField size="small" label={t('tc_contract_hours', 'Contract hours per week')} value={hours} onChange={(e) => setHours(e.target.value)}
                        placeholder="38" helperText={t('tc_contract_hint', 'Leave empty when unknown. Used for the difference per week.')} />
                    {employee.is_administrator ? (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('tc_admin_is_manager', 'Member of the Administrator group: always a time clock manager.')}</Typography>
                    ) : (
                        <FormControlLabel disabled={!me.is_administrator}
                            control={<Switch checked={manager} onChange={(e) => setManager(e.target.checked)} />}
                            label={<Box>
                                <Typography sx={{ fontSize: 14 }}>{t('tc_manager_right', 'Time clock manager')}</Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {me.is_administrator ? t('tc_manager_hint', 'Approves corrections and sees everyone\'s hours.') : t('tc_manager_admin_only', 'Only an administrator can appoint managers.')}
                                </Typography>
                            </Box>} />
                    )}
                    {employee.has_eid && (
                        <FormControlLabel control={<Switch checked={clearEid} onChange={(e) => setClearEid(e.target.checked)} color="error" />}
                            label={<Typography sx={{ fontSize: 14 }}>{t('tc_clear_eid', 'Unlink eID card')}</Typography>} />
                    )}
                    {error && <Typography sx={{ color: tone.bad, fontSize: 13.5 }}>{error}</Typography>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={busy} sx={{ textTransform: 'none' }}>{t('save', 'Save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TimeClockEmployeesTab;
